/**
 * ResourceContactsCard - shared, presentational Contacts card.
 *
 * Extracted from the Claims record's "Claim Contacts" card (WP1-57 /
 * WPS-141) so any resource (claims, transfers, ...) that wants the same
 * "static reference contacts + managed internal/external contacts" UI can
 * reuse the exact same component instead of a bespoke reimplementation.
 *
 * This component is PURELY PRESENTATIONAL: it takes data + callbacks as
 * props and calls no data-fetching hooks itself, so it is backend-agnostic.
 * Each caller (a thin per-resource wrapper) owns its own hooks (contacts
 * query, add/update/remove mutations, org members, roles) and adapts them
 * to the props below.
 */

import { useCallback, useId, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { Home, Mail, Pencil, Plus, User, Users, X } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { CreatableSelect } from "@/shared/components/ui/creatable-select";
import { DetailCard } from "@/admin/components/shared/detail-card";
import { ConfirmDeleteDialog } from "@/admin/components/confirm-delete-dialog";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, formatPhoneDisplay } from "@/shared/lib/forms";
import type { FormField } from "@/shared/lib/forms";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ContactPermission = "view_only" | "collaborator";

/**
 * Unified contact shape - a superset of the fields both `ClaimContact` and
 * `TransferContact` already return. `role` (the role FK uuid) and
 * `has_portal_access` are optional since not every resource's serializer
 * carries them the same way.
 */
export interface ManagedContact {
  uuid: string;
  user: string | null;
  external_name: string;
  external_email: string;
  external_phone: string;
  role_label: string;
  permission: ContactPermission;
  permission_label: string;
  added_by: string | null;
  added_by_name: string | null;
  display_name: string;
  display_email: string;
  display_phone: string;
  is_internal: boolean;
  has_portal_access?: boolean;
  is_main_contact?: boolean;
  role?: string | null;
  created_at?: string;
}

export interface ContactRoleOption {
  uuid: string;
  label: string;
  value: string;
}

export interface ContactOrgMember {
  user_uuid: string;
  user_email: string;
  user_name: string;
}

export interface StaticContactInfo {
  role: string;
  name?: string;
  email?: string;
  phone?: string;
}

/** Normalised payload the card hands back to `onAdd` / `onUpdate`. Callers
 *  pick whichever of `role` (a role-row uuid, for a resource whose write
 *  endpoint takes a role FK) or `role_label` (free text, for a resource
 *  whose write endpoint takes a label) their backend expects. */
export interface ManagedContactPayload {
  user?: string | null;
  external_name?: string;
  external_email?: string;
  external_phone?: string;
  permission?: ContactPermission;
  is_main_contact?: boolean;
  role: string | null;
  role_label: string;
}

export interface ResourceContactsCardProps {
  title?: string;
  icon?: ReactNode;
  /** Extra content in the DetailCard header trailing slot, alongside the
   *  card's own concerns (e.g. claims' inspection-contact-edit pencil). */
  trailing?: ReactNode;
  collapsible?: boolean;
  contacts: ManagedContact[];
  isLoading: boolean;
  canEdit: boolean;
  /** Whether the managed-contacts section (rows, add button, dialogs) is
   *  offered at all. Defaults to true; a caller gates this on its own
   *  capability check (e.g. claims' `claim_contact` entity registration). */
  showManagedSection?: boolean;
  orgMembers: ContactOrgMember[];
  roles?: ContactRoleOption[];
  /** Fired whenever the Add-contact dialog opens/closes, so a caller whose
   *  `orgMembers` query is itself lazy (only fetched while the team-member
   *  picker is reachable) can gate it on this instead of always fetching. */
  onAddOpenChange?: (open: boolean) => void;
  onAdd: (payload: ManagedContactPayload) => Promise<void>;
  onUpdate: (contactUuid: string, payload: ManagedContactPayload) => Promise<void>;
  onRemove: (contactUuid: string) => Promise<void>;
  /** Resolve-or-create a reusable role from a typed label. Optional - when
   *  omitted, the Role field behaves as plain free text. */
  onCreateRole?: (label: string) => Promise<{ value: string }>;
  /** Read-only reference contacts (e.g. the homeowner, an inspection
   *  contact) rendered above the managed-contacts list. */
  staticContacts?: {
    primary?: StaticContactInfo;
    secondary?: StaticContactInfo[];
  };
}

type ContactKind = "primary" | "reference" | "internal" | "external";

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------

/** Circular avatar whose tint is the internal / external / reference
 *  differentiator: a soft gray = reference contact, the brand accent =
 *  internal team member, a soft amber = external party. */
function ContactAvatar({ kind }: { kind: ContactKind }) {
  const config = {
    primary: {
      Icon: Home,
      className: "bg-[var(--status-info-bg)] text-[var(--status-info)]",
    },
    reference: {
      Icon: User,
      className: "bg-[var(--status-info-bg)] text-[var(--status-info)]",
    },
    internal: {
      Icon: User,
      className: "bg-[var(--accent)] text-[var(--accent-foreground)]",
    },
    external: {
      Icon: Mail,
      className: "bg-[var(--status-warn-bg)] text-[var(--status-warn)]",
    },
  }[kind];
  const { Icon } = config;
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${config.className}`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );
}

/** Shared avatar + title / subtitle + trailing-actions row. Every contact -
 *  the static primary/reference blocks and the managed internal/external
 *  rows - renders through this so the card reads consistently. */
function ContactRow({
  kind,
  name,
  email,
  phone,
  badges,
  portalAccess,
  actions,
  onClick,
}: {
  kind: ContactKind;
  name?: string;
  email?: string;
  phone?: string;
  badges?: ReactNode;
  portalAccess?: boolean;
  actions?: ReactNode;
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`group relative flex items-start gap-3 border-b-[0.5px] border-b-[var(--rule-light)] px-4 py-3 transition-colors last:border-b-0 ${
        clickable ? "cursor-pointer hover:bg-[var(--bg-subtle)]" : ""
      }`}
      onClick={onClick}
    >
      <ContactAvatar kind={kind} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[13px] font-medium text-[var(--ink)]">
            {name || "-"}
          </span>
          {badges}
        </div>
        {email ? (
          <a
            href={`mailto:${email}`}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 block truncate text-[12px] text-[var(--ink-3)] hover:text-[var(--green)] hover:underline"
          >
            {email}
          </a>
        ) : null}
        {phone ? (
          <p className="mt-0.5 truncate text-[12px] text-[var(--ink-3)]">
            {formatPhoneDisplay(phone) || phone}
          </p>
        ) : null}
        {portalAccess ? (
          <p className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
            {t("claimContacts.hasPortalAccess", "Has portal access")}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 focus-within:opacity-100 group-hover:opacity-100"
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function ContactBlock({
  contact,
  primary,
}: {
  contact: StaticContactInfo;
  primary?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <ContactRow
      kind={primary ? "primary" : "reference"}
      name={contact.name}
      email={contact.email}
      phone={contact.phone}
      badges={
        primary ? (
          <Badge
            variant="secondary"
            className="text-[9.5px] uppercase tracking-[0.08em]"
          >
            {t("claimContacts.primaryBadge", "Primary")}
          </Badge>
        ) : contact.role ? (
          <Badge
            variant="outline"
            className="text-[9.5px] uppercase tracking-[0.08em]"
          >
            {contact.role}
          </Badge>
        ) : null
      }
    />
  );
}

function ManagedContactRow({
  contact,
  canEdit,
  onEdit,
  onRequestRemove,
  removing,
}: {
  contact: ManagedContact;
  canEdit: boolean;
  onEdit: () => void;
  onRequestRemove: () => void;
  removing: boolean;
}) {
  const { t } = useTranslation();
  // External contacts are editable, so their whole row opens the editor;
  // internal contacts have nothing editable here (name/email are their user
  // account, permission is always full), so the row is not a click target.
  const rowEditable = canEdit && !contact.is_internal;
  return (
    <ContactRow
      kind={contact.is_internal ? "internal" : "external"}
      name={contact.display_name}
      email={contact.display_email || undefined}
      phone={contact.display_phone || undefined}
      portalAccess={!contact.is_internal && !!contact.has_portal_access}
      onClick={rowEditable ? onEdit : undefined}
      badges={
        <>
          {contact.is_main_contact ? (
            <Badge
              variant="default"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {t("claimContacts.mainContactBadge", "Main")}
            </Badge>
          ) : null}
          {!contact.is_internal && contact.permission_label ? (
            <Badge
              variant="outline"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {contact.permission_label}
            </Badge>
          ) : null}
          {contact.role_label ? (
            <Badge
              variant="secondary"
              className="text-[9.5px] uppercase tracking-[0.08em]"
            >
              {contact.role_label}
            </Badge>
          ) : null}
        </>
      }
      actions={
        canEdit ? (
          <>
            {!contact.is_internal ? (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onEdit}
                aria-label={t("claimContacts.editAria", "Edit {{name}}", {
                  name: contact.display_name,
                })}
                className="text-[var(--ink-3)] hover:text-[var(--ink)]"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRequestRemove}
              disabled={removing}
              aria-label={t("claimContacts.removeAria", "Remove {{name}}", {
                name: contact.display_name,
              })}
              className="text-[var(--ink-3)] hover:text-[var(--status-error)]"
            >
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : null
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Role field helper - a CreatableSelect so a typed label resolves (or
// creates) a reusable, tenant-scoped role. When `onCreateRole` is omitted
// the field behaves as plain free text (the typed value becomes both the
// selected value and the label).
// ---------------------------------------------------------------------------

function getContactPermissionOptions(t: TFunction) {
  return [
    {
      value: "view_only",
      label: t("claimContacts.permissionViewOnly", "View only"),
    },
    {
      value: "collaborator",
      label: t("claimContacts.permissionCollaborator", "Collaborator"),
    },
  ];
}

function buildContactRoleField({
  t,
  roleOptions,
  onCreateRole,
  initialValue = "",
}: {
  t: TFunction;
  roleOptions: { value: string; label: string }[];
  onCreateRole?: (label: string, onCreated: (value: string) => void) => void;
  initialValue?: string;
}): FormField {
  const roleLabel = t("claimContacts.fieldRole", "Role");
  return {
    type: "custom",
    name: "role",
    label: roleLabel,
    value: initialValue,
    render: (formContext) => {
      const value = (formContext.values.role ?? "") as string;
      return (
        <div className="flex flex-col gap-1.5">
          <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
            {roleLabel}
          </Label>
          <CreatableSelect
            value={value}
            onValueChange={(v) => formContext.setFieldValue("role", v)}
            onCreate={
              onCreateRole
                ? (label) =>
                    onCreateRole(label, (createdValue) =>
                      formContext.setFieldValue("role", createdValue),
                    )
                : undefined
            }
            options={roleOptions}
            placeholder={t(
              "claimContacts.roleSelectPlaceholder",
              "Select or create a role",
            )}
            createLabel={t("claimContacts.roleCreateLabel", "Create new role")}
            createPlaceholder={t(
              "claimContacts.roleCreatePlaceholder",
              "Type a role and press Enter",
            )}
          />
        </div>
      );
    },
  };
}

/** Tracks value -> label for roles, including ones just created locally (the
 *  `roles` prop may not have re-fetched by submit time), so the payload's
 *  `role_label` is always resolvable regardless of which write shape (role
 *  FK vs free-text label) the caller's backend needs. */
function useRoleLabelLookup(roles: ContactRoleOption[]) {
  const [localLabels, setLocalLabels] = useState<Record<string, string>>({});
  const lookup = useCallback(
    (value: string) =>
      localLabels[value] ??
      roles.find((r) => r.uuid === value)?.label ??
      value,
    [localLabels, roles],
  );
  const remember = useCallback((value: string, label: string) => {
    setLocalLabels((prev) => ({ ...prev, [value]: label }));
  }, []);
  return { lookup, remember };
}

// ---------------------------------------------------------------------------
// Add / Edit dialogs
// ---------------------------------------------------------------------------

function AddManagedContactDialog({
  open,
  onOpenChange,
  formKey,
  orgMembers,
  roles,
  onCreateRoleProp,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formKey: string;
  orgMembers: ContactOrgMember[];
  roles: ContactRoleOption[];
  onCreateRoleProp?: (label: string) => Promise<{ value: string }>;
  onSubmit: (payload: ManagedContactPayload) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"internal" | "external">("internal");
  const { lookup: lookupRoleLabel, remember: rememberRoleLabel } =
    useRoleLabelLookup(roles);

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.uuid, label: r.label })),
    [roles],
  );
  const handleCreateRole = useCallback(
    (label: string, onCreated: (value: string) => void) => {
      if (!onCreateRoleProp) return;
      onCreateRoleProp(label)
        .then(({ value }) => {
          rememberRoleLabel(value, label);
          onCreated(value);
        })
        .catch((err) =>
          toastApiError(
            err,
            t("claimContacts.failedToCreateRole", "Failed to create role"),
          ),
        );
    },
    [onCreateRoleProp, rememberRoleLabel, t],
  );

  const fields = useMemo<FormField[]>(() => {
    const roleField = buildContactRoleField({
      t,
      roleOptions,
      onCreateRole: onCreateRoleProp ? handleCreateRole : undefined,
    });
    if (mode === "internal") {
      return [
        {
          type: "select",
          name: "user",
          label: t("claimContacts.fieldTeamMember", "Team member"),
          value: "",
          placeholder: t(
            "claimContacts.selectTeamMemberPlaceholder",
            "Select a team member",
          ),
          options: orgMembers.map((m) => ({
            value: m.user_uuid,
            label: m.user_name
              ? `${m.user_name} (${m.user_email})`
              : m.user_email,
          })),
          required: t(
            "claimContacts.teamMemberRequired",
            "Please select a team member",
          ),
        },
        // No role field for internal team members - they are identified by
        // their platform role (RBAC). Role is an external-contact
        // attribute only.
      ];
    }
    return [
      {
        type: "text",
        name: "external_name",
        label: t("claimContacts.fieldName", "Name"),
        value: "",
        placeholder: t("claimContacts.namePlaceholder", "Joseph Rhodes"),
      },
      {
        type: "email",
        name: "external_email",
        label: t("claimContacts.fieldEmail", "Email"),
        value: "",
        placeholder: t(
          "claimContacts.emailPlaceholder",
          "joseph.rhodes@example.com",
        ),
        required: t("claimContacts.emailRequired", "Email is required"),
      },
      {
        type: "phone",
        name: "external_phone",
        label: t("claimContacts.fieldPhone", "Phone"),
        value: "",
      },
      roleField,
      {
        type: "select",
        name: "permission",
        label: t("claimContacts.fieldPermission", "Permission"),
        value: "view_only",
        options: getContactPermissionOptions(t),
      },
      {
        type: "checkbox",
        name: "is_main_contact",
        label: t("claimContacts.fieldMainContact", "Main contact"),
        value: false,
      },
    ];
  }, [mode, orgMembers, roleOptions, onCreateRoleProp, handleCreateRole, t]);

  const { Form, FormFooter } = useForm(
    {
      name: `${formKey}-add-${mode}`,
      submit: "footer",
      fields,
    },
    async (rawValues) => {
      const values = rawValues as {
        user?: string;
        external_name?: string;
        external_email?: string;
        external_phone?: string;
        role?: string;
        permission?: string;
        is_main_contact?: boolean;
      };
      const payload: ManagedContactPayload =
        mode === "internal"
          ? { user: values.user, role: null, role_label: "" }
          : {
              external_name: values.external_name,
              external_email: values.external_email,
              external_phone: values.external_phone,
              role: values.role || null,
              role_label: values.role ? lookupRoleLabel(values.role) : "",
              permission: values.permission as ContactPermission | undefined,
              is_main_contact: Boolean(values.is_main_contact),
            };
      try {
        await onSubmit(payload);
        toast.success(t("claimContacts.contactAdded", "Contact added"));
        onOpenChange(false);
      } catch (err) {
        toastApiError(
          err,
          t("claimContacts.failedToAddContact", "Failed to add contact"),
        );
        throw err;
      }
    },
    [mode, orgMembers, roleOptions, onCreateRoleProp, onSubmit, t],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setMode("internal");
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("claimContacts.addContact", "Add Contact")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "claimContacts.addDialogDescription",
              "Add a team member or an external party to this record.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "internal" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-[10px] uppercase tracking-[0.1em]"
            onClick={() => setMode("internal")}
          >
            {t("claimContacts.fieldTeamMember", "Team member")}
          </Button>
          <Button
            type="button"
            variant={mode === "external" ? "default" : "outline"}
            size="sm"
            className="flex-1 text-[10px] uppercase tracking-[0.1em]"
            onClick={() => setMode("external")}
          >
            {t("claimContacts.modeExternal", "External")}
          </Button>
        </div>
        <Form className="flex flex-col gap-3" />
        <DialogFooter className="mt-2">
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={t("claimContacts.addSubmit", "Add contact")}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditManagedContactDialog({
  open,
  onOpenChange,
  formKey,
  contact,
  roles,
  onCreateRoleProp,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formKey: string;
  /** External contact being edited; the dialog renders nothing when null. */
  contact: ManagedContact | null;
  roles: ContactRoleOption[];
  onCreateRoleProp?: (label: string) => Promise<{ value: string }>;
  onSubmit: (payload: ManagedContactPayload) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { lookup: lookupRoleLabel, remember: rememberRoleLabel } =
    useRoleLabelLookup(roles);

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.uuid, label: r.label })),
    [roles],
  );
  const handleCreateRole = useCallback(
    (label: string, onCreated: (value: string) => void) => {
      if (!onCreateRoleProp) return;
      onCreateRoleProp(label)
        .then(({ value }) => {
          rememberRoleLabel(value, label);
          onCreated(value);
        })
        .catch((err) =>
          toastApiError(
            err,
            t("claimContacts.failedToCreateRole", "Failed to create role"),
          ),
        );
    },
    [onCreateRoleProp, rememberRoleLabel, t],
  );

  const fields = useMemo<FormField[]>(() => {
    if (!contact) return [];
    return [
      {
        type: "text",
        name: "external_name",
        label: t("claimContacts.fieldName", "Name"),
        value: contact.external_name ?? "",
      },
      {
        type: "email",
        name: "external_email",
        label: t("claimContacts.fieldEmail", "Email"),
        value: contact.external_email ?? "",
        required: t("claimContacts.emailRequired", "Email is required"),
      },
      {
        type: "phone",
        name: "external_phone",
        label: t("claimContacts.fieldPhone", "Phone"),
        value: contact.external_phone ?? "",
      },
      buildContactRoleField({
        t,
        roleOptions,
        onCreateRole: onCreateRoleProp ? handleCreateRole : undefined,
        // A resource whose write endpoint carries a real role FK (claims)
        // always has `contact.role` (a role uuid) - use it directly so the
        // picker shows the tied role. A resource that only carries a
        // free-text `role_label` (transfers) has no uuid: resolve the
        // matching role by label so re-selecting it stays tied to the same
        // reusable role, falling back to the raw label as free text when it
        // doesn't match any known role (e.g. a legacy custom label).
        initialValue:
          contact.role ??
          roles.find((r) => r.label === contact.role_label)?.uuid ??
          contact.role_label ??
          "",
      }),
      {
        type: "select",
        name: "permission",
        label: t("claimContacts.fieldPermission", "Permission"),
        value: contact.permission ?? "view_only",
        options: getContactPermissionOptions(t),
      },
      {
        type: "checkbox",
        name: "is_main_contact",
        label: t("claimContacts.fieldMainContact", "Main contact"),
        value: contact.is_main_contact ?? false,
      },
    ];
  }, [contact, roles, roleOptions, onCreateRoleProp, handleCreateRole, t]);

  const { Form, FormFooter } = useForm(
    {
      name: `${formKey}-edit-${contact?.uuid ?? "none"}`,
      submit: "footer",
      fields,
    },
    async (rawValues) => {
      if (!contact) return;
      const values = rawValues as {
        external_name?: string;
        external_email?: string;
        external_phone?: string;
        role?: string;
        permission?: string;
        is_main_contact?: boolean;
      };
      const payload: ManagedContactPayload = {
        external_name: values.external_name,
        external_email: values.external_email,
        external_phone: values.external_phone,
        role: values.role || null,
        role_label: values.role ? lookupRoleLabel(values.role) : "",
        permission: values.permission as ContactPermission | undefined,
        is_main_contact: Boolean(values.is_main_contact),
      };
      try {
        await onSubmit(payload);
        toast.success(t("claimContacts.contactUpdated", "Contact updated"));
        onOpenChange(false);
      } catch (err) {
        toastApiError(
          err,
          t(
            "claimContacts.failedToUpdateContact",
            "Failed to update contact",
          ),
        );
        throw err;
      }
    },
    [contact, roleOptions, onCreateRoleProp, onSubmit, t],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("claimContacts.editDialogTitle", "Edit Contact")}
          </DialogTitle>
        </DialogHeader>
        {contact ? <Form className="flex flex-col gap-3" /> : null}
        <DialogFooter className="mt-2">
          {FormFooter && (
            <FormFooter
              onCancel={() => onOpenChange(false)}
              submitText={t("claimContacts.saveChanges", "Save changes")}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ResourceContactsCard({
  title = "Contacts",
  icon,
  trailing,
  collapsible,
  contacts,
  isLoading,
  canEdit,
  showManagedSection = true,
  orgMembers,
  roles = [],
  onAddOpenChange,
  onAdd,
  onUpdate,
  onRemove,
  onCreateRole,
  staticContacts,
}: ResourceContactsCardProps) {
  const { t } = useTranslation();
  // useId() can contain colons; strip them so the value stays a clean HTML
  // id / form-attribute token.
  const formKey = useId().replace(/:/g, "");

  const [addOpen, setAddOpenState] = useState(false);
  const setAddOpen = useCallback(
    (next: boolean) => {
      setAddOpenState(next);
      onAddOpenChange?.(next);
    },
    [onAddOpenChange],
  );
  const [editingContact, setEditingContact] = useState<ManagedContact | null>(
    null,
  );
  const [confirmRemoveUuid, setConfirmRemoveUuid] = useState<string | null>(
    null,
  );
  const [removingUuid, setRemovingUuid] = useState<string | null>(null);

  const primary = staticContacts?.primary;
  const secondary = staticContacts?.secondary ?? [];
  const hasPrimary = !!(primary && (primary.name || primary.email || primary.phone));
  const realSecondary = secondary.filter((c) => c.name || c.email || c.phone);

  // Load -> error -> empty -> content: never flash "no contacts" while the
  // managed-contacts query is still in flight.
  const managedReady = !showManagedSection || !isLoading;
  const managedEmpty = !showManagedSection || contacts.length === 0;
  const showEmptyState =
    !hasPrimary && realSecondary.length === 0 && managedReady && managedEmpty;
  const showAdd = canEdit && showManagedSection;

  const handleAdd = useCallback(
    (payload: ManagedContactPayload) => onAdd(payload),
    [onAdd],
  );
  const handleUpdate = useCallback(
    (payload: ManagedContactPayload) => {
      if (!editingContact) return Promise.resolve();
      return onUpdate(editingContact.uuid, payload);
    },
    [editingContact, onUpdate],
  );

  const handleRemove = async () => {
    if (!confirmRemoveUuid) return;
    setRemovingUuid(confirmRemoveUuid);
    try {
      await onRemove(confirmRemoveUuid);
      toast.success(t("claimContacts.contactRemoved", "Contact removed"));
      setConfirmRemoveUuid(null);
    } catch (err) {
      toastApiError(
        err,
        t("claimContacts.failedToRemoveContact", "Failed to remove contact"),
      );
    } finally {
      setRemovingUuid(null);
    }
  };

  if (
    !hasPrimary &&
    realSecondary.length === 0 &&
    !canEdit &&
    !showManagedSection
  ) {
    return null;
  }

  return (
    <>
      <DetailCard
        title={title}
        icon={icon ?? <Users className="h-4 w-4" />}
        trailing={trailing}
        collapsible={collapsible}
      >
        {hasPrimary && primary ? (
          <ContactBlock contact={primary} primary />
        ) : null}
        {realSecondary.map((c, i) => (
          <ContactBlock key={`sec-${c.role}-${i}`} contact={c} />
        ))}
        {showManagedSection && isLoading ? (
          <div className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
            {t("claimContacts.loadingContacts", "Loading contacts...")}
          </div>
        ) : null}
        {showManagedSection && !isLoading
          ? contacts.map((contact) => (
              <ManagedContactRow
                key={contact.uuid}
                contact={contact}
                canEdit={canEdit}
                onEdit={() => setEditingContact(contact)}
                onRequestRemove={() => setConfirmRemoveUuid(contact.uuid)}
                removing={removingUuid === contact.uuid}
              />
            ))
          : null}
        {showEmptyState ? (
          <div className="px-4 py-[9px] text-[12px] text-[var(--ink-3)]">
            {t(
              "claimContacts.noContactsYet",
              "No additional contacts captured yet.",
            )}
          </div>
        ) : null}
        {showAdd ? (
          <div className="px-4 py-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-[6px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg)] text-[11px] uppercase tracking-[0.06em] text-[var(--ink-2)] transition-all duration-150 hover:border-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--ink)]"
              data-testid="resource-contact-add"
            >
              <Plus className="h-3.5 w-3.5" />
              {t("claimContacts.addContact", "Add Contact")}
            </Button>
          </div>
        ) : null}
      </DetailCard>

      {showManagedSection ? (
        <>
          <AddManagedContactDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            formKey={formKey}
            orgMembers={orgMembers}
            roles={roles}
            onCreateRoleProp={onCreateRole}
            onSubmit={handleAdd}
          />
          <EditManagedContactDialog
            open={editingContact !== null}
            onOpenChange={(next) => {
              if (!next) setEditingContact(null);
            }}
            formKey={formKey}
            contact={editingContact}
            roles={roles}
            onCreateRoleProp={onCreateRole}
            onSubmit={handleUpdate}
          />
          <ConfirmDeleteDialog
            open={confirmRemoveUuid !== null}
            onOpenChange={(open) => {
              if (!open) setConfirmRemoveUuid(null);
            }}
            title={t("claimContacts.removeContactTitle", "Remove contact?")}
            description={t(
              "claimContacts.removeContactDescription",
              "This removes the contact from this record. They will no longer receive updates.",
            )}
            confirmLabel={t("claimContacts.removeConfirmLabel", "Remove")}
            isPending={removingUuid !== null}
            onConfirm={handleRemove}
          />
        </>
      ) : null}
    </>
  );
}
